/**
 * ConfigProvider: React context provider for the unified config system.
 * Loads configuration from the new config.* tables and constructs a ConfigResolver.
 *
 * Follows the same architectural patterns as SiteConfigContext.tsx:
 * - Reducer-based state management
 * - Retry with exponential backoff
 * - Cache fallback for graceful degradation
 */

import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { usePreviewHost } from '../../context/PreviewHostContext';
import SiteConfigContext, { type SiteConfigContextValue } from '../../context/SiteConfigContext';
import { setConversationWorkspace } from '../../services/conversationScope';
import {
    createInboundXService,
    fetchApiHealth,
    type InboundXService,
    SessionStorageUnavailableError,
    setActiveInboundXService,
} from '../../services/inboundx';
import {
    getResumeSessionIdFromUrl,
    hydrateResumedConversation,
} from '../../services/resumeConversation';
import { logger } from '../../utils/core/logger';
import { executeWithRetry, extractErrorMessage } from '../../utils/core/retryUtils';
import { isUrlAllowed } from '../../utils/domain/siteRestrictions';
import { autoFillEmailFromUrl } from '../../utils/web/emailAutoFill';
import { subscribeToUrlChanges } from '../../utils/web/urlChange';
import { getDeploymentTarget } from '../deployment';
import { setSiteConfigContext } from '../SiteConfigManager';
import { ConfigResolver } from './ConfigResolver';
import { setConfigLoadFailure } from './configLoadDiagnostics';
import { type DomainMatchStrategy, doesDomainMatchResolver } from './domainMatching';
import {
    fetchActiveDomainExperiment,
    fetchActiveStaffExperiment,
    fetchConfigDefinitions,
    fetchWidgetConfig,
} from './queries';

// ============================================================================
// ERRORS
// ============================================================================

/** Thrown when domain is not found in the domains table. Expected condition — not a bug. */
class DomainNotConfiguredError extends Error {
    readonly domain: string;
    constructor(domain: string) {
        super(`Domain not configured: ${domain}`);
        this.name = 'DomainNotConfiguredError';
        this.domain = domain;
    }
}

/** Thrown when config.configs table returns 0 definitions. Always a data/permissions issue. */
class EmptyDefinitionsError extends Error {
    constructor() {
        super(
            'Config definitions table returned 0 rows — check config.configs table data and RLS policies'
        );
        this.name = 'EmptyDefinitionsError';
    }
}

// ============================================================================
// CONTEXT TYPES
// ============================================================================

export interface ConfigContextValue {
    resolver: ConfigResolver | null;
    isLoaded: boolean;
    isLoading: boolean;
    error: string | null;
}

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

// ============================================================================
// REDUCER
// ============================================================================

interface ConfigState {
    status: 'idle' | 'loading' | 'ready' | 'error';
    resolver: ConfigResolver | null;
    error: string | null;
    service: InboundXService | null;
    apiVersion: string | null;
    environment: string | null;
    domainMatchStrategy: DomainMatchStrategy;
}

type ConfigAction =
    | { type: 'LOADING' }
    | {
          type: 'READY';
          resolver: ConfigResolver;
          service: InboundXService;
          apiVersion: string;
          environment: string;
          domainMatchStrategy: DomainMatchStrategy;
      }
    | { type: 'ERROR'; error: string }
    | {
          type: 'DEGRADED';
          resolver: ConfigResolver;
          error: string;
          domainMatchStrategy: DomainMatchStrategy;
      };

function configReducer(state: ConfigState, action: ConfigAction): ConfigState {
    switch (action.type) {
        case 'LOADING':
            return { ...state, status: 'loading', error: null };
        case 'READY':
            return {
                status: 'ready',
                resolver: action.resolver,
                error: null,
                service: action.service,
                apiVersion: action.apiVersion,
                environment: action.environment,
                domainMatchStrategy: action.domainMatchStrategy,
            };
        case 'ERROR':
            return {
                ...state,
                status: 'error',
                error: action.error,
                service: state.service,
                apiVersion: state.apiVersion,
                environment: state.environment,
            };
        case 'DEGRADED':
            return {
                status: 'ready',
                resolver: action.resolver,
                error: action.error,
                service: state.service,
                apiVersion: state.apiVersion,
                environment: state.environment,
                domainMatchStrategy: action.domainMatchStrategy,
            };
    }
}

// ============================================================================
// ERROR CLASSIFICATION
// ============================================================================

type ConfigErrorType =
    | 'supabase_unreachable'
    | 'api_unreachable'
    | 'timeout'
    | 'domain_not_configured'
    | 'empty_definitions'
    | 'unknown';

/** Classify config load errors so Sentry tags show WHAT failed and WHERE. */
function classifyConfigError(err: unknown): { type: ConfigErrorType; source: string } {
    if (err instanceof DomainNotConfiguredError) {
        return { type: 'domain_not_configured', source: 'supabase' };
    }
    if (err instanceof EmptyDefinitionsError) {
        return { type: 'empty_definitions', source: 'supabase' };
    }
    const msg = extractErrorMessage(err);
    const isApiSource = msg.includes('api.userose') || msg.includes('health');
    if (msg.includes('timeout') || msg.includes('Timeout') || msg.includes('aborted')) {
        return { type: 'timeout', source: isApiSource ? 'api' : 'supabase' };
    }
    if (
        msg.includes('Failed to fetch') ||
        msg.includes('NetworkError') ||
        msg.includes('Load failed')
    ) {
        return {
            type: isApiSource ? 'api_unreachable' : 'supabase_unreachable',
            source: isApiSource ? 'api' : 'supabase',
        };
    }
    return { type: 'unknown', source: 'unknown' };
}

// ============================================================================
// CACHE FOR GRACEFUL DEGRADATION
// ============================================================================

interface CachedResolver {
    resolver: ConfigResolver;
    domainMatchStrategy: DomainMatchStrategy;
    timestamp: number;
}

const resolverCache = new Map<string, CachedResolver>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCachedResolver(domain: string): CachedResolver | null {
    const cached = resolverCache.get(domain);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached;
    }
    return null;
}

function setCachedResolver(
    domain: string,
    resolver: ConfigResolver,
    domainMatchStrategy: DomainMatchStrategy
): void {
    resolverCache.set(domain, {
        resolver,
        domainMatchStrategy,
        timestamp: Date.now(),
    });
}

function createManagerContext(
    resolver: ConfigResolver,
    domainMatchStrategy: DomainMatchStrategy
): NonNullable<Parameters<typeof setSiteConfigContext>[0]> {
    const resolverDomain = resolver.domain;
    const domainMatchesResolver = (domain: string): boolean =>
        doesDomainMatchResolver(domain, resolverDomain, domainMatchStrategy);

    return {
        currentResolver: resolver,
        getResolver: (domain: string) => (domainMatchesResolver(domain) ? resolver : undefined),
        isSiteSupported: (domain: string) => domainMatchesResolver(domain),
        isCurrentURLAllowed: (domain: string) => {
            if (!domainMatchesResolver(domain)) return false;
            const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
            return isUrlAllowed(
                {
                    display_only_on_urls: resolver.traffic_control.display_only_on_urls,
                    custom_config: {
                        page_display_rules: resolver.traffic_control.page_display_rules,
                    },
                },
                currentUrl
            );
        },
        isLoaded: true,
    };
}

// ============================================================================
// PROVIDER
// ============================================================================

export interface ConfigProviderProps {
    children: ReactNode;
    effectiveDomain?: string;
    apiUrl?: string;
    apiKey?: string;
    /** When true, apiUrl takes absolute precedence over backoffice api_endpoint config. */
    forceApiUrl?: boolean;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({
    children,
    effectiveDomain,
    apiUrl,
    apiKey,
    forceApiUrl = false,
}) => {
    const [state, dispatch] = useReducer(configReducer, {
        status: 'idle',
        resolver: null,
        error: null,
        service: null,
        apiVersion: null,
        environment: null,
        domainMatchStrategy: 'normalized' as const,
    });

    const mountedRef = useRef(true);
    const explicitRoutingRef = useRef(false);
    const loadGenerationRef = useRef(0);
    // Read through a ref, not a dependency: the host object changes identity on every turn
    // (its answer-addon closure does), and depending on it would rebuild the service and
    // reset the conversation mid-test.
    const previewHost = usePreviewHost();
    const previewHostRef = useRef(previewHost);
    previewHostRef.current = previewHost;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const loadConfig = useCallback(async () => {
        const generation = ++loadGenerationRef.current;
        // Determine domain
        const rawDomain =
            effectiveDomain || (typeof window !== 'undefined' ? window.location.hostname : '');

        if (!rawDomain) {
            dispatch({ type: 'ERROR', error: 'No domain available' });
            return;
        }

        const requestedDomain = rawDomain.trim().toLowerCase();
        setConfigLoadFailure(requestedDomain, null);
        const pageUrl = previewHostRef.current?.pageUrl;
        const page = pageUrl
            ? new URL(pageUrl)
            : typeof window !== 'undefined'
              ? window.location
              : null;
        const pathname = page?.hostname === requestedDomain ? page.pathname : '/';
        const cacheKey = `${requestedDomain}${pathname}`;
        dispatch({ type: 'LOADING' });
        setSiteConfigContext(null);

        try {
            // onError intentionally omitted — the catch block below handles all
            // error reporting with full classification tags and cache fallback.
            const { resolver, domainMatchStrategy } = await executeWithRetry(async () => {
                // Step 1: Resolve domain match, client-enabled state, and the
                // per-domain config overrides in a single scoped RPC call. The
                // anon widget no longer reads the config tables directly.
                // The Playground passes its session so a route whose display is off
                // can be previewed; visitors' widgets have no session.
                const previewToken = await (previewHostRef.current?.getAuthToken?.() ??
                    Promise.resolve(null));
                let widgetConfig = await fetchWidgetConfig(
                    requestedDomain,
                    pathname,
                    undefined,
                    previewToken
                );
                // A workspace whose corpus was built outside production (apptest, a
                // local preview branch) is only ready in that environment. Ask the
                // API which one it serves and resolve once more; production widgets
                // never reach this. Private configs (and any api_endpoint override)
                // are withheld while unavailable, so the loader's apiUrl is the API.
                if (widgetConfig?.workspaceUnavailable && apiUrl) {
                    const environment = (await fetchApiHealth(apiUrl).catch(() => null))
                        ?.environment;
                    if (environment && environment !== 'production') {
                        widgetConfig = await fetchWidgetConfig(
                            requestedDomain,
                            pathname,
                            environment,
                            previewToken
                        );
                    }
                }
                if (!widgetConfig) {
                    throw new DomainNotConfiguredError(requestedDomain);
                }
                const { domain: domainRow, matchStrategy, client, clientConfigs } = widgetConfig;
                if (generation !== loadGenerationRef.current)
                    throw new Error('Superseded config load');
                explicitRoutingRef.current = Boolean(
                    widgetConfig.workspaceId || widgetConfig.workspaceUnavailable
                );

                // Step 2: Fetch definitions and the active experiments (staff-wide
                // + per-domain) in parallel. get_widget_config returns the experiments
                // (IX-5784); the anon experiment RPCs remain the fallback while the
                // database still runs an older function.
                const [definitions, staffExperiment, domainExperiment] = await Promise.all([
                    fetchConfigDefinitions(),
                    widgetConfig.staffExperiment !== undefined
                        ? widgetConfig.staffExperiment
                        : fetchActiveStaffExperiment(domainRow.domain),
                    widgetConfig.domainExperiment !== undefined
                        ? widgetConfig.domainExperiment
                        : fetchActiveDomainExperiment(domainRow.domain),
                ]);

                if (definitions.length === 0) {
                    throw new EmptyDefinitionsError();
                }

                // Step 3: Construct resolver
                return {
                    resolver: new ConfigResolver(
                        domainRow,
                        client,
                        definitions,
                        clientConfigs,
                        [],
                        staffExperiment,
                        domainExperiment
                    ),
                    domainMatchStrategy: matchStrategy,
                };
            });

            if (!mountedRef.current || generation !== loadGenerationRef.current) return;

            // If the client is disabled, the resolver returns null for ALL configs
            // (including non-features like identity/system), so we cannot activate the
            // adapter or render the widget. Bail early.
            if (!resolver.clientEnabled) {
                logger.info('ConfigProvider: client is disabled', { domain: requestedDomain });
                dispatch({ type: 'ERROR', error: 'Client is disabled' });
                return;
            }

            setSiteConfigContext(createManagerContext(resolver, domainMatchStrategy));
            // Before the service reads its session: each workspace on a host keeps its own
            // conversation. Widget storage is keyed by the visited host, the service's by
            // the matched domain. A hosting surface scopes it itself, before mounting.
            // A client can opt out and keep one conversation across its workspaces.
            if (!previewHostRef.current) {
                const workspaceId =
                    resolver.traffic_control.conversation_per_workspace === false
                        ? undefined
                        : resolver.workspaceId;
                setConversationWorkspace(requestedDomain, workspaceId);
                setConversationWorkspace(resolver.domain, workspaceId);
            }

            // Cache for degraded mode
            setCachedResolver(cacheKey, resolver, domainMatchStrategy);

            // Step 4: Create InboundXService and fetch API health
            //
            // Health check is INTENTIONALLY FATAL — it acts as a circuit breaker.
            // If the browser cannot reach api.userose.ai, the widget cannot function
            // (no chat, no streaming, no AI responses). Rendering a non-functional
            // widget that looks interactive but silently fails on every user action
            // would be worse UX than not rendering at all.
            //
            // Common causes of failure:
            // - Client not configured to use the API proxy
            // - User's browser or corporate firewall blocking api.userose.ai
            //
            // This is needed so AnalyticsProvider gets apiVersion/environment/service via SiteConfigContext bridge.
            // Use backoffice api_endpoint override if set, otherwise fall back to loader apiUrl.
            // When forceApiUrl is true (playground custom endpoint), skip backoffice override.
            const effectiveApiUrl = forceApiUrl ? apiUrl : resolver.system.api_endpoint || apiUrl;

            if (effectiveApiUrl) {
                if (resolver.system.api_endpoint && !forceApiUrl) {
                    logger.info('ConfigProvider: Using backoffice api_endpoint override', {
                        override: resolver.system.api_endpoint,
                        loaderApiUrl: apiUrl,
                    });
                }
                const service = createInboundXService({
                    apiUrl: effectiveApiUrl,
                    apiKey,
                    siteName: resolver.domain,
                    workspaceRouting: explicitRoutingRef.current,
                    pageUrl,
                    getPreviewAuthToken: () =>
                        previewHostRef.current?.getAuthToken?.() ?? Promise.resolve(null),
                });

                // Publish it for surfaces outside the React tree (WebMCP tool
                // registration), so they share this endpoint and this session
                // rather than standing up a second service of their own.
                setActiveInboundXService(service);

                // IX-3738: A ?rose_resume=<session_id> deep-link (from the ChatGPT-app card)
                // continues an MCP conversation on-site. This must resolve HERE, before
                // analytics reads getSessionId(), so every event of the visit carries the
                // adopted session id. On failure (expired/foreign id) we boot normally.
                const resumeSessionId = getResumeSessionIdFromUrl();
                if (resumeSessionId) {
                    await hydrateResumedConversation(service, requestedDomain, resumeSessionId);
                }

                // IX-4266: re-entry no longer rotates the session id. The id and the
                // transcript both live in sessionStorage, and the server thread is keyed
                // on the id — rotating one without the other would restore a conversation
                // the agent has no memory of. They now expire together with the tab.
                // (This replaces the IX-2261 forceNewSession-before-analytics ordering:
                // with no rotation there is no session_T1/session_T2 split to order.)

                const { version, environment: env } = await executeWithRetry(
                    () => service.getHealth(),
                    {
                        onRetry: (attempt, _error, delay) => {
                            logger.debug(
                                `ConfigProvider: Retrying API health (attempt ${attempt + 2}), waiting ${delay}ms`
                            );
                        },
                    }
                );

                if (!mountedRef.current || generation !== loadGenerationRef.current) return;

                if (!version || !env) {
                    logger.error(
                        'ConfigProvider: API version or environment missing from health response'
                    );
                    dispatch({ type: 'ERROR', error: 'API version or environment missing' });
                    return;
                }

                logger.info('ConfigProvider: Service ready', { version, environment: env });

                autoFillEmailFromUrl();

                dispatch({
                    type: 'READY',
                    resolver,
                    service,
                    apiVersion: version,
                    environment: env,
                    domainMatchStrategy,
                });
            } else {
                logger.warn(
                    'ConfigProvider: No API URL available (neither backoffice override nor loader apiUrl)'
                );
                dispatch({ type: 'ERROR', error: 'apiUrl required for ConfigProvider' });
            }
        } catch (err: unknown) {
            if (!mountedRef.current || generation !== loadGenerationRef.current) return;

            const errorClass = classifyConfigError(err);
            const errorMessage = extractErrorMessage(err);
            setConfigLoadFailure(requestedDomain, errorClass);

            if (err instanceof DomainNotConfiguredError) {
                // Expected condition — domain not in DB (proxy, staging, etc.)
                logger.info('ConfigProvider: domain not configured', { domain: err.domain });
            } else if (err instanceof SessionStorageUnavailableError) {
                // Expected condition — iOS Lockdown Mode, sandboxed iframe,
                // disabled storage. Widget cannot guarantee session continuity
                // so we skip render rather than degrading silently. Cache
                // fallback wouldn't help: a degraded resolver still needs an
                // InboundXService, which can't be built without storage.
                logger.info('ConfigProvider: sessionStorage unavailable, skipping widget', {
                    domain: requestedDomain,
                    reason: err.reason,
                });
                setSiteConfigContext(null);
                dispatch({ type: 'ERROR', error: 'sessionStorage unavailable' });
                return;
            } else {
                logger.error('ConfigProvider: failed to load config', err, {
                    domain: requestedDomain,
                    config_error_type: errorClass.type,
                    error_source: errorClass.source,
                });
            }

            // Build a user-actionable message when Supabase is blocked
            const dispatchMessage =
                errorClass.type === 'supabase_unreachable'
                    ? `Config load failed: cannot reach Supabase (${errorMessage}). ` +
                      `This usually means the user's browser is blocking direct Supabase connections. ` +
                      `Ensure this client is configured to use the API proxy.`
                    : errorMessage;

            // Try cache fallback
            const cached = explicitRoutingRef.current ? null : getCachedResolver(cacheKey);
            if (cached) {
                logger.info('ConfigProvider: using cached resolver for degraded mode');
                setSiteConfigContext(
                    createManagerContext(cached.resolver, cached.domainMatchStrategy)
                );
                dispatch({
                    type: 'DEGRADED',
                    resolver: cached.resolver,
                    error: dispatchMessage,
                    domainMatchStrategy: cached.domainMatchStrategy,
                });
            } else {
                setSiteConfigContext(null);
                dispatch({ type: 'ERROR', error: dispatchMessage });
            }
        }
    }, [effectiveDomain, apiUrl, apiKey, forceApiUrl]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    useEffect(() => {
        if (!state.resolver || !explicitRoutingRef.current) return;
        let pathname = window.location.pathname;
        return subscribeToUrlChanges(() => {
            const nextPath = window.location.pathname;
            if (nextPath === pathname) return;
            pathname = nextPath;
            if (explicitRoutingRef.current) void loadConfig();
        });
    }, [loadConfig, state.resolver]);

    const value = useMemo<ConfigContextValue>(
        () => ({
            resolver: state.resolver,
            isLoaded: state.status === 'ready',
            isLoading: state.status === 'loading',
            error: state.error,
        }),
        [state.resolver, state.status, state.error]
    );

    // Bridge: provide resolver-native access via SiteConfigContext for consumers
    // that still need domain matching, load state, or the shared service instance.
    const siteConfigBridge = useMemo<SiteConfigContextValue>(() => {
        const resolver = state.resolver;
        const isReady = state.status === 'ready' && !!resolver;
        const resolverDomain = resolver?.domain ?? '';
        const domainMatchStrategy = state.domainMatchStrategy;
        const domainMatchesResolver = (domain: string): boolean =>
            doesDomainMatchResolver(domain, resolverDomain, domainMatchStrategy);

        return {
            currentDomain: isReady ? resolver.domain : null,
            currentResolver: isReady ? resolver : null,
            isLoaded: isReady,
            isLoading: state.status === 'loading',
            error: state.error,
            deploymentTarget: getDeploymentTarget(),
            apiVersion: state.apiVersion ?? undefined,
            environment: state.environment ?? undefined,
            inboundXService: state.service,
            getResolver: (domain: string) =>
                domainMatchesResolver(domain) && resolver ? resolver : undefined,
            isSiteSupported: (domain: string) => domainMatchesResolver(domain),
            isCurrentURLAllowed: (domain: string) => {
                if (!domainMatchesResolver(domain) || !resolver) return false;
                const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
                return isUrlAllowed(
                    {
                        display_only_on_urls: resolver.traffic_control.display_only_on_urls,
                        custom_config: {
                            page_display_rules: resolver.traffic_control.page_display_rules,
                        },
                    },
                    currentUrl
                );
            },
        };
    }, [
        state.resolver,
        state.status,
        state.error,
        state.service,
        state.apiVersion,
        state.environment,
        state.domainMatchStrategy,
    ]);

    return (
        <ConfigContext.Provider value={value}>
            <SiteConfigContext.Provider value={siteConfigBridge}>
                {children}
            </SiteConfigContext.Provider>
        </ConfigContext.Provider>
    );
};

// ============================================================================
// HOOKS
// ============================================================================

/** Access the full config context value. */
export function useConfig(): ConfigContextValue {
    const ctx = useContext(ConfigContext);
    if (!ctx) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return ctx;
}

/**
 * Access the config context value without throwing when ConfigProvider is absent.
 * For shared components (e.g. MarkdownRenderer, ContentRecommendationCard) that
 * render both inside the widget (with ConfigProvider) and in admin/backoffice
 * surfaces (without it).
 */
export function useOptionalConfig(): ConfigContextValue | undefined {
    return useContext(ConfigContext);
}

/** Access the config resolver directly. Throws if not loaded yet. */
export function useConfigResolver(): ConfigResolver {
    const { resolver } = useConfig();
    if (!resolver) {
        throw new Error('Config not loaded yet. Ensure ConfigProvider is mounted and loaded.');
    }
    return resolver;
}
